import React, { useState, useEffect } from 'react';
import { createModel } from 'hox';
import { TablePaginationConfig } from 'antd/es/table/interface';
import { DEFAULT_FIRST_PAGE_NUM, DEFAULT_PAGE_SIZE, defaultMdCmParam } from '@/utils/constants';
import {
	ICustomerDetail,
	ICustomerSearchFields,
	customerService,
	ICustomerInsert,
	ICustomerUpdate,
} from './customerService';
import { globalPrompt } from '@/components/message';
import useGlobalModel, { transPgToPagination } from '@/model/globalModel';
import { convertRes2Blob } from '@/utils/exportFile';
import { ISearchPage } from '@/types/AppTypes';
import moment from 'moment';
type ISCMFormVal = {
	qualifications?: string[];
	[key: string]: any;
};

export const useCustomerModel = createModel(function () {
	const {
		user: { shopCode, shopName, userName },
		setLoadingInfo,
	} = useGlobalModel(({ user }) => [user]);
	const { sourceApp, branch } = defaultMdCmParam;
	//列表数据源
	const [dataSource, setDataSource] = useState<ICustomerDetail[]>([]);
	const [loading, setLoading] = useState<boolean>(false);
	//查询条件
	const [searchParam, setSearchParam] = useState<ICustomerSearchFields>({});
	//分页参数
	const initPgCfg: TablePaginationConfig = {
		pageSize: DEFAULT_PAGE_SIZE,
		current: DEFAULT_FIRST_PAGE_NUM,
	};
	const [pgCfg, setPgCfg] = useState<TablePaginationConfig>(initPgCfg);
	//表格多选id值
	const [selectedRowKeys, setSelectedRowKeys] = useState<ICustomerDetail[]>([]);

	//列表查询
	const loadData = async (page?: ISearchPage, searchValue: ICustomerSearchFields = searchParam) => {
		setLoading(true);
		const searchParams = {
			shopCode,
			...searchValue,
			pageSize: page?.pageSize ?? pgCfg.pageSize ?? DEFAULT_PAGE_SIZE,
			pageNum: (page?.current || page?.pageNum) ?? pgCfg.current ?? DEFAULT_FIRST_PAGE_NUM,
		};
		let { retData } = await customerService.page(searchParams);
		const { records, ...pg } = retData;
		setPgCfg(transPgToPagination(pg));
		setDataSource(records);
		setLoading(false);
	};
	//设置搜索value
	const updateSearchParam = (updateParam: ICustomerSearchFields) => {
		setSearchParam({ ...searchParam, ...updateParam });
	};

	//重置搜索参数
	const resetSearchParam = () => {
		setSearchParam({});
		setSelectedRowKeys([]);
		loadData(initPgCfg, {});
	};

	/**
	 * 表单详情
	 */

	const initAddFormVal = {
		shopName,
		createUser: userName,
		status: true,
	};
	//编辑表单缓存
	const [editFormVal, setEditFormVal] = useState<any>({});

	//新增表单缓存
	const [addFormVal, setAddFormVal] = useState<any>(initAddFormVal);
	//表单新增修改
	const updateAddFormVal = (val: object | null) => {
		val ? setAddFormVal({ ...addFormVal, ...val }) : setAddFormVal(initAddFormVal);
	};
	//表单新增修改
	const updateEditFormVal = (val: object | null) => {
		val ? setEditFormVal({ ...editFormVal, ...val }) : setEditFormVal({});
	};
	//获取一条详情数据 Api
	const getCustomerOne = async (id: string) => {
		let { retData } = await customerService.one(id);
		retData &&
			setEditFormVal({
				...retData,
				contractEndTime: retData.contractEndTime === '' ? '' : moment(retData.contractEndTime),
				contractStartTime: retData.contractStartTime === '' ? '' : moment(retData.contractStartTime),
				qualifications: retData?.qualifications === '' ? [] : JSON.parse(retData?.qualifications ?? "[]"),
				createTime:retData.createTime === '' ? '' : moment(retData.createTime),
				updateTime:retData.updateTime === '' ? '' : moment(retData.updateTime),
			});
	};

	//表单新增 Api
	const insertCustomer = async (val: any) => {
		let data: ICustomerInsert = {
			...val,
			sourceApp,
			branch,
			shopCode,
			shopName,
			orgCode: '组织机构ID',
			orgName: '组织机构名称',
			status: val.status ? '1' : '0',
			createTime:val.createTime instanceof Object ?  val.createTime.format('YYYY-MM-DDTHH:mm:ss') :null,
			updateTime:val.updateTime instanceof Object ?  val.updateTime.format('YYYY-MM-DDTHH:mm:ss') :null,
		};
		let { retData } = await customerService.insert(data);
		globalPrompt('message', { text: '客户新增成功', type: 'success' });
		return retData;
	};

	//表单编辑 Api
	const editCustomer = async (val: any & { id: string }) => {
		let data: ICustomerInsert = {
			...val,
			sourceApp,
			branch,
			shopCode,
			shopName,
			orgCode: '组织机构ID',
			orgName: '组织机构名称',
			status: val.status ? '1' : '0',
			createTime:val.createTime instanceof Object ?  val.createTime.format('YYYY-MM-DDTHH:mm:ss') :null,
			updateTime:val.updateTime instanceof Object ?  val.updateTime.format('YYYY-MM-DDTHH:mm:ss') :null,
		};
		let { retData } = await customerService.update(data);
		globalPrompt('message', { text: '客户编辑成功', type: 'success' });
		return retData;
	};

	//导出
	const exportCustomer = async (searchValue: ICustomerSearchFields = searchParam) => {
		setLoadingInfo({ loadingVisible: true, txt: '快速导出中' });
		try {
			const searchParams = {
				shopCode,
				...searchValue,
				id: selectedRowKeys.map((item) => item.id)?.join(),
				pageSize: -1,
				pageNum: 1,
			};

			let result = await customerService.export(searchParams);
			result && setLoadingInfo({ loadingVisible: false, txt: '' });
			convertRes2Blob(result);
		} catch {
			setLoadingInfo({ loadingVisible: false, txt: '' });
		}
	};

	//下载模板
	const downloadTpl = async () => {
		let result = await customerService.downloadTpl();
		convertRes2Blob(result);
	};

	//启用 禁用 api
	const disableCustomer = async (code: string, status: number) => {
		let { retData } = await customerService.disable({ code, shopCode, status });
		loadData();
		globalPrompt('message', { text: '操作成功', type: 'success' });
	};

	return {
		/***field***/
		searchParam,
		dataSource,
		selectedRowKeys,
		pgCfg,
		editFormVal,
		addFormVal,
		loading,
		userName,
		/***method***/
		updateAddFormVal,
		updateEditFormVal,
		insertCustomer,
		editCustomer,
		getCustomerOne,
		setDataSource,
		setSelectedRowKeys,
		resetSearchParam,
		updateSearchParam,
		loadData,
		exportCustomer,
		downloadTpl,
		disableCustomer,
	};
});
